import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 161511 $
// Purpose     : react components for vl type system
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Checkbox, Image, Menu, Icon, Button, Segment, Table } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { keys, isNil, uniq, get, strip, isString, isInteger, mapItems, sorted, values, flatten, isObject, groupBy, dict, size } from 'ldash';
import log from 'uix/logging';
import { useSelector, usePersistent, useGraphics, useState, useEffectSSR, useSummaries, useMemo, useApi } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { ucFirst } from 'uix/utils';
import { ResizableDialog, Link, RGrid, XMenu } from 'uix/components';
import { Graphics } from 'uix/graphics';
import { DomainLabel } from 'uix/domain/label';
// ---Local imports
import typeStyles from './styles.css';
// ---Exports
export const useChosenTypes = ()=>{
    /* usePersistent with query (&types=...) parameter override support
   */ let request = useSelector(Sel.request);
    let [from_query_initialized, set_from_query_initialized] = useState(false);
    let [value, set_value] = usePersistent('TypeChooser', []);
    let decode = (qt)=>qt.split(',').filter((e)=>e);
    useEffectSSR(()=>{
        // initialization phase
        let qt = request.query.types;
        if (qt !== undefined) {
            qt = qt || '';
            if (qt != value.join(',')) {
                // update persistence
                set_value(decode(qt));
            }
        }
        // switch to persisted value now
        // XXX: what if set_from_query_initialized() is faster than set_value() ?
        if (!from_query_initialized) set_from_query_initialized(true);
    }, [
        request
    ]);
    if (request.query.types !== undefined && !from_query_initialized) {
        // return decoded query param during initialization
        return [
            decode(request.query.types || ''),
            set_value
        ];
    }
    return [
        value,
        set_value
    ];
};
const defaultDatatypeNames = (dconfig)=>[
        'topstructure',
        'metadata',
        'substructure',
        'page',
        dconfig.names.on && 'name',
        dconfig.places.on && 'place',
        dconfig.subjects.on && 'subject'
    ].filter((e)=>e);
// useChosenTypes hook is not usable in class based part
// so hack a selector
export const selectTypeNames = (root, partName)=>get(root.ui.byId, `Part/${partName}/TypeChooser.value`, []);
export const TypeChooser = ({ disabled, filteredDatatype, subdomains })=>{
    let graphics = useGraphics([
        'types'
    ]);
    let dconfig = useSelector(Sel.dconfig);
    let dataTypes = filteredDatatype ? [
        filteredDatatype
    ] : defaultDatatypeNames(dconfig);
    let [open, setOpen] = useState(false);
    let types = useSelector(Sel.types);
    let { data } = useApi('types/getSubdomainTypes', {}, {
        cached: true,
        condition: open && subdomains
    });
    types = useMemo(()=>{
        let sdtypes = data?.subdomainTypes || {};
        return dict(dataTypes.map((dt)=>[
                dt,
                uniq([
                    ...keys(types[dt]),
                    ...sdtypes[dt] ?? []
                ])
            ]));
    }, [
        types,
        data
    ]);
    let i18n = useSelector(Sel.i18n);
    let [typeNames, setTypeNames] = useChosenTypes();
    let [activeTypes, setActiveTypes] = useState(typeNames);
    let [closedGroups, setClosedGroups] = usePersistent('TypeChooserClosed', defaultDatatypeNames(dconfig), 2);
    closedGroups = isNil(filteredDatatype) ? closedGroups : [];
    let onCancel = ()=>{
        setActiveTypes(typeNames);
        setOpen(false);
    };
    let toggle = (val)=>{
        if (activeTypes.includes(val)) {
            activeTypes = activeTypes.filter((n)=>n !== val);
        } else {
            activeTypes = [
                ...activeTypes,
                val
            ];
        }
        setActiveTypes(activeTypes);
    };
    let countActive = (datatype)=>activeTypes.filter((n)=>types[datatype].includes(n)).length;
    let countAvailable = (datatype)=>size(types[datatype]);
    let clearGroup = (datatype)=>setActiveTypes(activeTypes.filter((n)=>!types[datatype].includes(n) && n !== `_${datatype}_`));
    let toggleOpenGroup = (val)=>{
        if (closedGroups.includes(val)) {
            closedGroups = closedGroups.filter((n)=>n !== val);
        } else {
            closedGroups = [
                ...closedGroups,
                val
            ];
        }
        setClosedGroups(closedGroups);
    };
    // add datatypes with len(subtypes) == 0
    let closedGroupsAndEmpty = [
        ...closedGroups,
        ...dataTypes.filter((dt)=>countAvailable(dt) == 0)
    ];
    let groups_sorted = sorted(dataTypes, {
        //~ key: child => i18n.get(`types_datatype_${child}`),
        key: (dt)=>[
                2,
                1,
                0,
                3,
                13,
                16,
                19
            ].indexOf(dt),
        localcmp: true
    });
    let groups = groups_sorted.map((datatype)=>{
        let children = types[datatype].map((child)=>/*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    width: '250px',
                    marginTop: '.5rem'
                },
                id: child,
                checked: activeTypes.includes(child) || activeTypes.includes(`_${datatype}_`),
                disabled: activeTypes.includes(`_${datatype}_`),
                onChange: (e, data)=>toggle(child),
                label: /*#__PURE__*/ _jsxs("label", {
                    htmlFor: child,
                    children: [
                        graphics.types && /*#__PURE__*/ _jsx(Image, {
                            className: typeStyles.labelImage,
                            src: graphics.types[`${datatype}/${child}`]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: i18n.getType(child)
                        })
                    ]
                })
            }, child));
        let children_sorted = sorted(children, {
            key: (child)=>i18n.getType(child.key),
            localcmp: true
        });
        let groupActive = activeTypes.includes(`_${datatype}_`);
        let indeterminate = !groupActive && countActive(datatype) > 0;
        return /*#__PURE__*/ _jsxs("div", {
            className: clsx(typeStyles.group, closedGroupsAndEmpty.includes(datatype) && typeStyles.closed),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: typeStyles.headline,
                    children: [
                        /*#__PURE__*/ _jsx(Checkbox, {
                            checked: groupActive,
                            indeterminate: indeterminate,
                            label: /*#__PURE__*/ _jsxs("label", {
                                children: [
                                    /*#__PURE__*/ _jsx(DatatypeDescription, {
                                        name: datatype
                                    }),
                                    indeterminate ? ` (${countActive(datatype)})` : null
                                ]
                            }),
                            style: {
                                color: 'grey'
                            },
                            onClick: ()=>toggle(`_${datatype}_`)
                        }),
                        /*#__PURE__*/ _jsxs(XMenu, {
                            padded: true,
                            size: "tiny",
                            children: [
                                /*#__PURE__*/ _jsx(XMenu.Item, {
                                    icon: "eraser",
                                    onClick: ()=>clearGroup(datatype),
                                    size: "tiny",
                                    title: "Clear",
                                    disabled: !groupActive && countActive(datatype) < 0
                                }),
                                /*#__PURE__*/ _jsx(XMenu.Item, {
                                    icon: closedGroupsAndEmpty.includes(datatype) ? 'chevron down' : 'chevron up',
                                    onClick: ()=>toggleOpenGroup(datatype),
                                    disabled: countAvailable(datatype) == 0
                                })
                            ]
                        })
                    ]
                }),
                !closedGroupsAndEmpty.includes(datatype) && /*#__PURE__*/ _jsx("div", {
                    className: typeStyles.content,
                    children: children_sorted
                })
            ]
        }, datatype);
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Menu.Item, {
                onClick: ()=>setOpen(true),
                disabled: disabled,
                title: `${i18n.get('datatype')}`,
                children: [
                    /*#__PURE__*/ _jsxs(Icon.Group, {
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                name: "filter"
                            }),
                            " ",
                            /*#__PURE__*/ _jsx(Icon, {
                                corner: true,
                                name: "folder outline"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DatatypeIcons, {
                        activeTypes: activeTypes
                    })
                ]
            }),
            open && /*#__PURE__*/ _jsx(ResizableDialog, {
                closeOnEsc: true,
                onClose: ()=>{
                    setOpen(false);
                },
                onCancel: onCancel,
                onSubmit: ()=>{
                    setTypeNames(activeTypes);
                    setOpen(false);
                },
                icon: 'filter',
                header: i18n.get('datatype'),
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        padding: '1rem'
                    },
                    children: groups
                })
            })
        ]
    });
};
const DatatypeIcons = ({ activeTypes })=>{
    let itypes = useSelector(Sel.itypes);
    let graphics = useGraphics([
        'types'
    ]);
    let graphic_names = uniq(activeTypes.map((typeName)=>{
        let dt = itypes.byName[typeName]?.[0];
        if (typeName.startsWith('_')) {
            dt = itypes.datatypeMapInverse[strip(typeName, '_')];
        }
        return `${dt}_0`;
    }));
    let icon = (n)=>/*#__PURE__*/ _jsx(Image, {
            style: {
                marginLeft: 3
            },
            inline: true,
            width: "14px",
            src: graphics.types?.[n]
        }, n);
    return graphic_names.map(icon);
};
export const TypeIcon = ({ name, datatype, subtype, title, width = 14, style })=>{
    let s = name ? name : `${datatype}_${subtype}`;
    return /*#__PURE__*/ _jsx(Image, {
        title: title,
        inline: true,
        style: style,
        width: width,
        src: Graphics.types && Graphics.types[s],
        className: typeStyles.icon
    });
};
// type icon + custom label
export const TypeLabel = ({ content, children, style, as = 'span', icon = true, title, ...props })=>/*#__PURE__*/ React.createElement(as, {
        style,
        title,
        children: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                icon && /*#__PURE__*/ _jsx(TypeIcon, {
                    ...props
                }),
                " ",
                content || children
            ]
        })
    });
// datatype icon + datatype translation
export const DatatypeDescription = ({ name, datatype, unlabeled = false, ...props })=>{
    let i18n = useSelector(Sel.i18n);
    let itypes = useSelector(Sel.itypes);
    if (!isNil(datatype)) {
        name = itypes.datatypeMap[datatype];
    }
    let label = i18n.get(`types_datatype_${name}`);
    if (unlabeled) {
        props = {
            ...props,
            title: label
        };
    } else {
        props = {
            ...props,
            content: /*#__PURE__*/ _jsx("span", {
                children: label
            })
        };
    }
    return /*#__PURE__*/ _jsx(TypeLabel, {
        ...props,
        name: name
    });
};
// type icon + type translation
export const TypeDescription = ({ name, datatype, subtype, ...props })=>{
    let i18n = useSelector(Sel.i18n);
    props = {
        ...props,
        name,
        datatype,
        subtype
    };
    return /*#__PURE__*/ _jsx(TypeLabel, {
        ...props,
        content: i18n.getType(name || datatype, subtype)
    });
};
// node type icon + node.caption (with fallback to TypeDescription on empty caption)
export const TypeCaption = ({ caption, ...props })=>caption ? /*#__PURE__*/ _jsx(TypeLabel, {
        content: caption,
        ...props
    }) : /*#__PURE__*/ _jsx(TypeDescription, {
        ...props
    });
export const DomainNode = ({ dname, ...props })=>/*#__PURE__*/ _jsx(TreeNode, {
        ...props,
        node: {
            datatype: 10,
            dname
        }
    });
// also see useTreeNodes in db/hooks
export const TreeNode = ({ node, link = false, linkLazy = false, title, style, as = 'span' })=>{
    /* build a (linked) TypeCaption given a node={otid, odid, datatype, subtype} structure
  link can be `true` or classname/tabname routing segment
  */ let content = node.datatype === 10 ? /*#__PURE__*/ _jsx(DomainLabel, {
        dname: node.dname,
        odid: node.odid,
        style: style,
        as: as,
        title: title
    }) : /*#__PURE__*/ _jsx(TypeCaption, {
        ...node,
        style: style,
        as: as,
        title: title
    });
    if (link || linkLazy) {
        let id = node.otid;
        let [classname, tabname, ctxid, query, updateQuery] = Array(5).fill(undefined);
        if (isString(link)) {
            // fetch classname/tabname from link='custom/view'
            ;
            [classname, tabname] = link.split('/');
        } else if (isObject(link)) {
            // XXX: unpacking?
            classname = link.classname;
            tabname = link.tabname;
            ctxid = link.ctxid;
            query = link.query;
            updateQuery = link.updateQuery;
            if (link.id === null) {
                id = null;
            }
        }
        return /*#__PURE__*/ _jsx(Link, {
            dname: node.dname,
            odid: node.odid,
            classname: classname,
            tabname: tabname,
            ctxid: ctxid,
            id: id,
            query: query,
            updateQuery: updateQuery,
            lazy: linkLazy,
            style: style,
            children: content
        });
    }
    return content;
};
const decode_cfgcols = (query, cfg)=>{
    let all_names = flatten(mapItems(cfg, (n, cfg_data)=>{
        if (!isObject(cfg_data)) return [
            n
        ];
        // second level cfg
        return keys(cfg_data).map((k)=>`${n}.${k}`);
    }));
    let query_parts = query.split(',');
    //~ log.x('XX', cfgcols)
    return flatten(query_parts.map((qp)=>all_names.filter((n)=>qp.length > 2 && n.toLowerCase().includes(qp))));
};
export const TypesConfigTable = ({ datatypeName = 'metadata', cfgcols, query, withCounts = false, hideEmptyCounts = false, xdtypes })=>{
    let dtypes = useSelector(Sel.types);
    if (xdtypes) {
        dtypes = xdtypes;
    }
    let dt_cfg = dtypes[datatypeName];
    let i18n = useSelector(Sel.i18n);
    let { summaries } = useSummaries(withCounts ? [
        'metadata'
    ] : []);
    let metadataStats = summaries.metadata;
    if (isString(cfgcols)) {
        cfgcols = cfgcols.split(',').map((e)=>strip(e));
    } else if (query) {
        cfgcols = decode_cfgcols(query, values(dt_cfg)[0]);
    } else {
        cfgcols = [];
    }
    let headergroups = null;
    if (cfgcols.length > 1) {
        let colgroups = groupBy(cfgcols, (e)=>e.split('.')[0]);
        headergroups = /*#__PURE__*/ _jsxs(Table.Row, {
            children: [
                /*#__PURE__*/ _jsx(Table.HeaderCell, {}),
                mapItems(colgroups, (group, elements)=>/*#__PURE__*/ _jsx(Table.HeaderCell, {
                        colspan: elements.length,
                        children: /*#__PURE__*/ _jsx("b", {
                            children: group
                        })
                    }, group))
            ]
        });
    }
    let header = /*#__PURE__*/ _jsxs(Table.Row, {
        children: [
            /*#__PURE__*/ _jsx(Table.HeaderCell, {}),
            cfgcols.map((cfg_name)=>/*#__PURE__*/ _jsx(Table.HeaderCell, {
                    children: headergroups ? cfg_name.split('.').slice(-1)[0] : cfg_name
                }, cfg_name)),
            withCounts && /*#__PURE__*/ _jsx(Table.HeaderCell, {
                children: "Anzahl"
            })
        ]
    });
    let renderValue = (v)=>{
        let s = String(v);
        return /*#__PURE__*/ _jsx("tt", {
            children: s
        });
    };
    let rows = mapItems(dt_cfg || {}, (name, cfg)=>{
        let cells = cfgcols.map((cfg_name)=>/*#__PURE__*/ _jsx(Table.Cell, {
                children: renderValue(get(cfg, cfg_name))
            }, cfg_name));
        let label = i18n.getType(name);
        let count = metadataStats?.[cfg.internalType]?.total;
        let available = count ? true : false;
        let availableStyle = available ? null : {
            color: 'var(--vl_bodySecondary_color-hover)'
        };
        return [
            label,
            /*#__PURE__*/ _jsxs(Table.Row, {
                disabled: !available,
                children: [
                    /*#__PURE__*/ _jsx(Table.Cell, {
                        children: /*#__PURE__*/ _jsxs("span", {
                            style: availableStyle,
                            title: `subtype ${cfg.internalType}`,
                            children: [
                                /*#__PURE__*/ _jsx(TypeIcon, {
                                    name: name
                                }),
                                " ",
                                label
                            ]
                        })
                    }),
                    cells,
                    withCounts && /*#__PURE__*/ _jsx(Table.Cell, {
                        children: count
                    })
                ]
            }, name)
        ];
    }, (name, cfg)=>!hideEmptyCounts || metadataStats?.[cfg.internalType]?.total > 0);
    rows = sorted(rows, (e)=>e[0]).map((e)=>e[1]);
    return /*#__PURE__*/ _jsxs(Table, {
        definition: true,
        compact: "very",
        children: [
            /*#__PURE__*/ _jsxs(Table.Header, {
                children: [
                    headergroups,
                    header
                ]
            }),
            /*#__PURE__*/ _jsx(Table.Body, {
                children: rows
            })
        ]
    });
};
export const DatatypeConfigTable = ({ dtcfg })=>{
    if (!dtcfg) return null;
    let renderValue = (v)=>{
        return String(v);
    };
    let renderScalars = (sec, sec_name = '')=>mapItems(sec, (k, v)=>/*#__PURE__*/ _jsxs(Table.Row, {
                children: [
                    /*#__PURE__*/ _jsx(Table.Cell, {
                        style: {
                            paddingLeft: sec_name ? '30px' : undefined
                        },
                        children: /*#__PURE__*/ _jsx("tt", {
                            children: k
                        })
                    }),
                    /*#__PURE__*/ _jsx(Table.Cell, {
                        children: renderValue(v)
                    })
                ]
            }, k), (k, v)=>!isObject(v));
    let renderSecs = (sec)=>mapItems(sec, (k, v)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Table.Row, {
                        children: /*#__PURE__*/ _jsx(Table.Cell, {
                            colSpan: "2",
                            style: {
                                backgroundColor: 'rgba(0,0,0,0.05)'
                            },
                            children: /*#__PURE__*/ _jsx("tt", {
                                children: k
                            })
                        })
                    }),
                    renderScalars(v, k)
                ]
            }, k), (k, v)=>isObject(v));
    let table = (rows)=>/*#__PURE__*/ _jsxs(Table, {
            definition: true,
            compact: "very",
            children: [
                /*#__PURE__*/ _jsx(Table.Header, {
                    children: /*#__PURE__*/ _jsxs(Table.Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Table.HeaderCell, {
                                children: "Name"
                            }),
                            /*#__PURE__*/ _jsx(Table.HeaderCell, {
                                children: "Wert"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Table.Body, {
                    children: rows
                })
            ]
        });
    return /*#__PURE__*/ _jsx(RGrid, {
        stackable: true,
        children: /*#__PURE__*/ _jsxs(RGrid.Row, {
            children: [
                /*#__PURE__*/ _jsx(RGrid.Column, {
                    width: "8",
                    children: table(renderScalars(dtcfg))
                }),
                /*#__PURE__*/ _jsx(RGrid.Column, {
                    width: "8",
                    children: table(renderSecs(dtcfg))
                })
            ]
        })
    });
};
